import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { workflowService } from '../services/workflow.service';
import type { Workflow } from '../types/workflow.types';
import { useAuth } from '../contexts/AuthContext';

const createSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const ChatPage: React.FC = () => {
  const { user } = useAuth();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workflowId, setWorkflowId] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [sessionId, setSessionId] = useState(createSessionId());
  const [sending, setSending] = useState(false);
  const [lastExecutionId, setLastExecutionId] = useState<string | null>(null);

  const canExecute = useMemo(() => {
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    const perms = Array.isArray((user as any).permissions) ? (user as any).permissions : [];
    return perms.includes('workflow.execute');
  }, [user]);

  useEffect(() => {
    const loadWorkflows = async () => {
      try {
        setLoading(true);
        setError(null);
        const resp = await workflowService.getWorkflows({ page: 1, page_size: 200 });
        const results = resp.results || [];
        setWorkflows(results);
        if (results.length) {
          setWorkflowId((prev) => prev || results[0].id);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load workflows');
      } finally {
        setLoading(false);
      }
    };
    loadWorkflows();
  }, []); // only on mount — workflowId is not a data dependency here

  const handleSend = async () => {
    if (!workflowId) {
      enqueueSnackbar('Select a workflow first', { variant: 'error' });
      return;
    }
    if (!chatInput.trim()) {
      enqueueSnackbar('Enter a message', { variant: 'error' });
      return;
    }
    try {
      setSending(true);
      const resp = await workflowService.executeChatWorkflow(workflowId, chatInput.trim(), sessionId.trim() || undefined);
      setLastExecutionId(resp.execution_id);
      enqueueSnackbar('Chat execution started', { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to start chat', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
        Chat Runner
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Start a workflow with a chat message and session id.
      </Typography>

      {!canExecute && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You do not have permission to execute workflows.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        {loading && (
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && (
          <Alert severity="error">{error}</Alert>
        )}
        {!loading && !error && (
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="chat-workflow-label">Workflow</InputLabel>
              <Select
                labelId="chat-workflow-label"
                label="Workflow"
                value={workflowId}
                onChange={(e) => setWorkflowId(e.target.value)}
              >
                {workflows.map((wf) => (
                  <MenuItem key={wf.id} value={wf.id}>
                    {wf.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Session ID"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              size="small"
              fullWidth
              helperText="Use the same session ID to preserve memory."
            />

            <TextField
              label="Message"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={sending || !canExecute}
              >
                {sending ? 'Starting...' : 'Send'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setChatInput('');
                  setSessionId(createSessionId());
                  setLastExecutionId(null);
                }}
                disabled={sending}
              >
                Reset
              </Button>
            </Stack>

            {lastExecutionId && (
              <Alert severity="success">
                Execution started: {lastExecutionId}
              </Alert>
            )}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

